import React, { useEffect } from 'react';
import * as S from './Modal.styled';
import { useAnimatedVisibility } from '@/hooks/useAnimatedVisibility';
import { Portal } from '@/components/UI/Portal/Portal';

interface ModalProps {
  isOpen: boolean;
  maxWidth?: number;
  borderRadius?: number;
  onClose?: () => void;
  isScrollLocked?: boolean;
  dataTestId?: string;
  className?: string;
  children: React.ReactNode;
  background?: string;
  isInPortal?: boolean;
}

export const Modal = ({
  isOpen,
  onClose,
  children,
  maxWidth = 460,
  borderRadius = 14,
  dataTestId,
  isScrollLocked = false,
  className,
  background,
  isInPortal = false,
}: ModalProps) => {
  const { isVisible, onAnimationEnd } = useAnimatedVisibility(isOpen);

  useEffect(() => {
    if (isScrollLocked) {
      document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    }
  }, [isOpen, isScrollLocked]);

  if (!isVisible) return null;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClose?.();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleContentAnimationEnd = (e: React.AnimationEvent) => {
    if (e.target !== e.currentTarget) return;

    onAnimationEnd();
  };

  function renderModal() {
    return (
      <S.ModalWrapper data-testid={dataTestId}>
        <S.Backdrop
          onClick={handleClose}
          $isOpen={isOpen}
          data-testid="modal-backdrop"
          className="reveal-modal-bg"
        />
        <S.ModalContent
          $isOpen={isOpen}
          $maxWidth={maxWidth}
          $borderRadius={borderRadius}
          onClick={handleContentClick}
          data-testid="modal-content"
          className={className}
          $background={background}
          onAnimationEnd={handleContentAnimationEnd}
        >
          {children}
        </S.ModalContent>
      </S.ModalWrapper>
    );
  }

  if (isInPortal) {
    return <Portal>{renderModal()}</Portal>;
  }

  return renderModal();
};
